import Grid from '@mui/material/Grid';

import { ensureDefined } from '@e-socna/utils';
import { ResolverReturnTypeEnum } from '@e-socna/data-layer';

import { useReferencesContext } from '../../../../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import { useContextualizedFields } from '../../../../hooks/useContextualizedFields.hook';
import { DIALOG_AUTO_FOCUS_ATTRIBUTE } from '../../../../constants/dialog-auto-focus.constant';
import { AMOUNT_VALUE_FIELD_STEP } from '../../../../constants/number-field-steps.constant';
import type { ResolvableFieldSettings } from '../../../../components/ResolvableFields/types/resolvable-field-settings.type';

import { ResolvableField } from '../../../ResolvableField';

import type { ResolvableServiceForm_ResolvableServiceBaseInterface } from '../../fragments/resolvable-service-base-interface.fragment';

type ContextualizedFields = Readonly<
  Required<
    Pick<
      ResolvableServiceForm_ResolvableServiceBaseInterface['value'],
      'priceExcludingVat' | 'vatId' | 'discountPercentage' | 'discountAmount'
    >
  >
>;

interface Props {
  readonly autoFocus?: boolean;
  readonly formContext?: string;
  readonly id: string;
  readonly resolvableFieldSettings: ResolvableFieldSettings;
}

export const ResolvableServiceQuotationInfoFormStep = ({
  autoFocus = false,
  formContext,
  resolvableFieldSettings,
}: Readonly<Props>) => {
  // data needed in form
  const referencesContext = useReferencesContext();
  const { currenciesIdMap, vatsIdMap } = referencesContext;
  const { defaultCurrency, defaultVat } = ensureDefined({
    defaultCurrency: referencesContext.defaultCurrency,
    defaultVat: referencesContext.defaultVat,
  });

  const fieldsBase = useContextualizedFields<Readonly<ContextualizedFields>>(
    {
      priceExcludingVat: {
        id: 'quotation-info-price-excluding-vat',
        name: 'value.priceExcludingVat',
      },
      vatId: {
        id: 'quotation-info-vat-id',
        name: 'value.vatId',
      },
      discountPercentage: {
        id: 'quotation-info-discount-percentage',
        name: 'value.discountPercentage',
      },
      discountAmount: {
        id: 'quotation-info-discount-amount',
        name: 'value.discountAmount',
      },
    },
    formContext,
  );

  const fields = {
    ...fieldsBase,
    priceExcludingVatValue: {
      id: `${fieldsBase.priceExcludingVat.id}-value`,
      name: `${fieldsBase.priceExcludingVat.name}.value`,
      value: fieldsBase.priceExcludingVat.value.value,
    },
    priceExcludingVatCurrencyId: {
      id: `${fieldsBase.priceExcludingVat.id}-currencyId`,
      name: `${fieldsBase.priceExcludingVat.name}.currencyId`,
      value: fieldsBase.priceExcludingVat.value.currencyId,
    },
    discountAmountValue: {
      id: `${fieldsBase.discountAmount.id}-value`,
      name: `${fieldsBase.discountAmount.name}.value`,
      value: fieldsBase.discountAmount.value?.value,
    },
    discountAmountCurrencyId: {
      id: `${fieldsBase.discountAmount.id}-currencyId`,
      name: `${fieldsBase.discountAmount.name}.currencyId`,
      value: fieldsBase.discountAmount.value?.currencyId,
    },
  };

  return (
    <Grid container spacing={2} sx={{ alignContent: 'flex-start' }}>
      <Grid
        size={{
          sm: 6,
          xs: 12,
        }}
        sx={{ pt: 1, pb: 1 }}
      >
        <ResolvableField
          defaultStaticValue={1}
          id={fields.priceExcludingVatValue.id}
          label="Prix Hors Taxe"
          name={fields.priceExcludingVatValue.name}
          required
          resolvableFieldSettings={resolvableFieldSettings}
          slotProps={{
            htmlInput: {
              [DIALOG_AUTO_FOCUS_ATTRIBUTE]: autoFocus,
              step: AMOUNT_VALUE_FIELD_STEP,
            },
          }}
          type={ResolverReturnTypeEnum.Float}
          value={fields.priceExcludingVatValue.value}
        />
      </Grid>
      <Grid
        size={{
          sm: 6,
          xs: 12,
        }}
        sx={{ pt: 1, pb: 1 }}
      >
        <ResolvableField
          defaultStaticValue={defaultCurrency._id}
          id={fields.priceExcludingVatCurrencyId.id}
          idMap={currenciesIdMap}
          label="Devise du Prix Hors Taxe"
          name={fields.priceExcludingVatCurrencyId.name}
          required
          resolvableFieldSettings={resolvableFieldSettings}
          type={ResolverReturnTypeEnum.CurrencyId}
          value={fields.priceExcludingVatCurrencyId.value}
        />
      </Grid>
      <Grid size={12} sx={{ pt: 1, pb: 1 }}>
        <ResolvableField
          defaultStaticValue={defaultVat._id}
          id={fields.vatId.id}
          idMap={vatsIdMap}
          label="TVA"
          name={fields.vatId.name}
          required
          resolvableFieldSettings={resolvableFieldSettings}
          type={ResolverReturnTypeEnum.VatId}
          value={fields.vatId.value}
        />
      </Grid>
      <Grid size={12} sx={{ pt: 1, pb: 1 }}>
        <ResolvableField
          defaultStaticValue={0}
          id={fields.discountPercentage.id}
          label="Pourcentage de Remise"
          name={fields.discountPercentage.name}
          required
          resolvableFieldSettings={resolvableFieldSettings}
          slotProps={{
            htmlInput: {
              min: 0,
              step: AMOUNT_VALUE_FIELD_STEP,
            },
          }}
          type={ResolverReturnTypeEnum.Float}
          value={fields.discountPercentage.value}
        />
      </Grid>
      <Grid
        size={{
          sm: 6,
          xs: 12,
        }}
        sx={{ pt: 1, pb: 1 }}
      >
        <ResolvableField
          defaultStaticValue={0}
          id={fields.discountAmountValue.id}
          label="Montant Fixe de Remise"
          name={fields.discountAmountValue.name}
          required
          resolvableFieldSettings={resolvableFieldSettings}
          slotProps={{
            htmlInput: {
              min: 0,
              step: AMOUNT_VALUE_FIELD_STEP,
            },
          }}
          type={ResolverReturnTypeEnum.Float}
          value={fields.discountAmountValue.value}
        />
      </Grid>
      <Grid
        size={{
          sm: 6,
          xs: 12,
        }}
        sx={{ pt: 1, pb: 1 }}
      >
        <ResolvableField
          defaultStaticValue={defaultCurrency._id}
          id={fields.discountAmountCurrencyId.id}
          idMap={currenciesIdMap}
          label="Devise de la Remise"
          name={fields.discountAmountCurrencyId.name}
          required
          resolvableFieldSettings={resolvableFieldSettings}
          type={ResolverReturnTypeEnum.CurrencyId}
          value={fields.discountAmountCurrencyId.value}
        />
      </Grid>
    </Grid>
  );
};
